import React, {useCallback} from 'react';

import GenericNameTemplateField from 'name-templates/GenericNameTemplateField';
import {NameTemplatePart, NameTemplateType} from 'name-templates/NameTemplateConstants';
import SetNameTemplateModal, {SET_NAME_TEMPLATE_MODAL_KEY} from 'name-templates/SetNameTemplateModal';
import * as LegacyModalContainer from 'ui-library/LegacyModalContainer';
import {SettingsPopover} from 'ui-library/SettingsField';

export interface RequestV2NameTemplateFieldProps {
  definitionId: string;
  nameTemplateParts: Array<NameTemplatePart>;
  isWritable: boolean;
  setNameTemplateParts: (parts: Array<NameTemplatePart>) => Promise<boolean>;
}

export default function RequestV2NameTemplateField(props: RequestV2NameTemplateFieldProps): JSX.Element {
  const {definitionId, nameTemplateParts, isWritable, setNameTemplateParts} = props;

  const closeModal = useCallback((): void => {
    LegacyModalContainer.close(SET_NAME_TEMPLATE_MODAL_KEY);
  }, []);

  const openModal = useCallback((): void => {
    LegacyModalContainer.open(
      SET_NAME_TEMPLATE_MODAL_KEY,
      <SetNameTemplateModal
        bioentityType=""
        schemaFields={[]}
        sequenceType={null}
        schemaId={definitionId}
        initialNameTemplateParts={nameTemplateParts}
        nameTemplateType={NameTemplateType.REQUEST_V2}
        nameTemplateTypeConfiguration={undefined}
        itemType={null}
        onRequestSubmit={async (submitArgs) => {
          const didSubmit = await setNameTemplateParts(submitArgs.nameTemplateParts);
          if (didSubmit) {
            closeModal();
          }
        }}
        onRequestClose={closeModal}
      />
    );
  }, [definitionId, nameTemplateParts, setNameTemplateParts, closeModal]);

  const helpIconContent = (
    <SettingsPopover
      title="Request name template"
      renderContent={() => (
        <div className="tagSchemaSettings-popover">
          <p>
            You can automatically name request submissions based on a naming template. If the name template is
            changed, it will not affect the names of existing requests.
          </p>
        </div>
      )}
    />
  );

  return (
    <GenericNameTemplateField
      label="Request name template"
      sequenceType={null}
      bioentityType=""
      schemaId={definitionId}
      schemaFields={[]}
      nameTemplateParts={nameTemplateParts}
      isWritable={isWritable}
      helpIconContent={helpIconContent}
      setNameTemplateParts={openModal}
      isDeletable={true}
      removeNameTemplateParts={async () => {
        await setNameTemplateParts([]);
      }}
      testAttrComponent="RequestV2NameTemplateField"
      nameTemplateTypeConfiguration={undefined}
    />
  );
}
